import React, { useState } from 'react';
import { Volume2, Vibrate } from 'lucide-react';
import { HostSettingsLayout } from '../../components/HostSettingsLayout';
import { hostCardStyle } from '../../components/HostSettingsCard';
import {
  isSoundEnabled, setSoundEnabled,
  isHapticEnabled, setHapticEnabled,
  hapticTap, feedbackLike,
} from '../../utils/appFeedback';

const Row = ({ icon, label, help, right, onClick, testId }) => (
  <div
    role="button"
    tabIndex={onClick ? 0 : -1}
    onClick={onClick}
    onKeyDown={(e) => { if (onClick && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(); } }}
    data-testid={testId}
    style={{
      ...hostCardStyle,
      width: '100%',
      background: 'var(--surface)',
      cursor: onClick ? 'pointer' : 'default',
      padding: '14px 16px',
      display: 'flex', alignItems: 'center', gap: 12,
      textAlign: 'left',
      border: '1px solid var(--card-border)',
      outline: 'none',
    }}
  >
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 10, background: 'var(--surface-2)' }}>
      {icon}
    </span>
    <div style={{ flex: 1, minWidth: 0 }}>
      <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 16, color: 'var(--text)' }}>{label}</p>
      {help ? (
        <p style={{ margin: '2px 0 0', fontFamily: "'Inria Serif', serif", fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.35 }}>{help}</p>
      ) : null}
    </div>
    {right}
  </div>
);

const Toggle = ({ on, onChange, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    onClick={(e) => { e.stopPropagation(); onChange(!on); }}
    data-testid={testId}
    style={{
      width: 46, height: 26, borderRadius: 999,
      background: on ? 'var(--accent-purple)' : 'var(--surface-2)',
      border: '1px solid var(--card-border)',
      position: 'relative', cursor: 'pointer', transition: 'background 160ms ease',
      padding: 0,
    }}
  >
    <span style={{
      position: 'absolute',
      top: 2, left: on ? 22 : 2,
      width: 20, height: 20, borderRadius: '50%',
      background: '#FFFFFF',
      boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
      transition: 'left 160ms ease',
    }} />
  </button>
);

export default function GuestFeedbackSettingsPage() {
  const [sound, setSound] = useState(isSoundEnabled());
  const [haptic, setHaptic] = useState(isHapticEnabled());

  const toggleSound = (v) => { setSoundEnabled(v); setSound(v); if (v) feedbackLike(); };
  const toggleHaptic = (v) => { setHapticEnabled(v); setHaptic(v); if (v) hapticTap(); };

  const iconColor = 'var(--text)';

  return (
    <HostSettingsLayout title="Sound & Vibration" dataTestId="guest-feedback-settings-page">
      <Row
        icon={<Volume2 size={18} color={iconColor} strokeWidth={1.8} />}
        label="Sounds"
        help="Small in-app sounds. Muted phones stay muted."
        right={<Toggle on={sound} onChange={toggleSound} testId="feedback-sounds-toggle" />}
        onClick={() => toggleSound(!sound)}
        testId="feedback-sounds-row"
      />
      <Row
        icon={<Vibrate size={18} color={iconColor} strokeWidth={1.8} />}
        label="Vibration"
        help="Tactile feedback on taps and confirmations."
        right={<Toggle on={haptic} onChange={toggleHaptic} testId="feedback-haptic-toggle" />}
        onClick={() => toggleHaptic(!haptic)}
        testId="feedback-haptic-row"
      />
    </HostSettingsLayout>
  );
}
